import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, vModelText as _vModelText, withKeys as _withKeys, normalizeClass as _normalizeClass, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, Fragment as _Fragment, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, "Please set a new password for your account.", -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("label", {
  for: "newPassword",
  class: "block text-sm font-medium text-zinc-700"
}, " New Password ", -1)
const _hoisted_4 = { class: "mt-1" }
const _hoisted_5 = ["onKeyup"]
const _hoisted_6 = /*#__PURE__*/_createElementVNode("label", {
  for: "confirmPassword",
  class: "block text-sm font-medium text-zinc-700"
}, " Confirm New Password ", -1)
const _hoisted_7 = { class: "mt-1" }
const _hoisted_8 = ["onKeyup"]
const _hoisted_9 = ["disabled"]
const _hoisted_10 = /*#__PURE__*/_createElementVNode("svg", {
  class: "animate-spin -ml-1 mr-3 h-5 w-5 text-white",
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  viewBox: "0 0 24 24"
}, [
  /*#__PURE__*/_createElementVNode("circle", {
    class: "opacity-25",
    cx: "12",
    cy: "12",
    r: "10",
    stroke: "currentColor",
    "stroke-width": "4"
  }),
  /*#__PURE__*/_createElementVNode("path", {
    class: "opacity-75",
    fill: "currentColor",
    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
  })
], -1)
const _hoisted_11 = /*#__PURE__*/_createTextVNode(" Confirming Reset ")
const _hoisted_12 = /*#__PURE__*/_createTextVNode(" Confirm Password Reset ")

import { inject, onMounted, reactive, ref } from 'vue'
import { gsap } from 'gsap'
// Other modules
import { SignInStep } from '@/types'
import { passwordRegex } from '@/helpers'
// Components
import { ExclamationCircleIcon } from '@heroicons/vue/outline'
import { authStore } from '@/store/authStore'


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const form = reactive({
  newPassword: {
    value: '',
    error: false,
  },
  confirmPassword: {
    value: '',
    error: false,
  },
})

// STATE
const loading = ref(false)
const errorMessage = ref('')

const setStep = inject<(signInStep: SignInStep) => void>('setStep')

/* ERRORS + VALIDATION
 **********************/

function validPasswords() {
  if (!passwordRegex.test(form.newPassword.value)) {
    errorMessage.value =
      'Password must have a minimum length of 8 characters, and contain at least 1 uppercase letter, 1 lowercase letter and 1 number.'
    form.newPassword.error = true
    return false
  }
  if (form.newPassword.value !== form.confirmPassword.value) {
    errorMessage.value = 'Passwords do not match.'
    form.confirmPassword.error = true
    return false
  }
  return true
}

// DOM
const errorElement = ref<HTMLElement>()

function clearErrors() {
  form.newPassword.error = false
  form.confirmPassword.error = false
  if (!errorElement.value) return
  gsap.to(errorElement.value, { height: 0, marginTop: 0, opacity: 0, duration: 0.3 })
}

function showErrorMessage(message?: string) {
  if (message) {
    errorMessage.value = message
  }
  if (!errorElement.value) return
  gsap.to(errorElement.value, { height: 'auto', marginTop: '1rem', opacity: 1, duration: 0.3 })
}

async function confirmPasswordReset() {
  clearErrors()

  if (!validPasswords()) {
    showErrorMessage()
    return
  }

  try {
    loading.value = true
    // send to backend
    await authStore.completeNewPassword(form.newPassword.value)
    if (setStep) {
      setStep('complete')
    }
  } catch {
    errorMessage.value = `An error occured while requesting new password changes.`
    showErrorMessage()
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  clearErrors()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", null, [
      _hoisted_3,
      _createElementVNode("div", _hoisted_4, [
        _withDirectives(_createElementVNode("input", {
          id: "newPassword",
          type: "password",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(form).newPassword.value) = $event)),
          onFocus: clearErrors,
          onKeyup: _withKeys(confirmPasswordReset, ["enter"]),
          class: _normalizeClass(["appearance-none block w-full px-3 py-2 border border-zinc-300 rounded-md shadow-sm placeholder-zinc-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm", _unref(form).newPassword.error ? 'border-red-500' : ''])
        }, null, 42, _hoisted_5), [
          [
            _vModelText,
            _unref(form).newPassword.value,
            void 0,
            { trim: true }
          ]
        ])
      ])
    ]),
    _createElementVNode("div", null, [
      _hoisted_6,
      _createElementVNode("div", _hoisted_7, [
        _withDirectives(_createElementVNode("input", {
          id: "confirmPassword",
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(form).confirmPassword.value) = $event)),
          type: "password",
          onFocus: clearErrors,
          onKeyup: _withKeys(confirmPasswordReset, ["enter"]),
          class: _normalizeClass(["appearance-none block w-full px-3 py-2 border border-zinc-300 rounded-md shadow-sm placeholder-zinc-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm", _unref(form).confirmPassword.error ? 'border-red-500' : ''])
        }, null, 42, _hoisted_8), [
          [
            _vModelText,
            _unref(form).confirmPassword.value,
            void 0,
            { trim: true }
          ]
        ])
      ])
    ]),
    _createElementVNode("button", {
      onClick: confirmPasswordReset,
      disabled: loading.value,
      class: "w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
    }, [
      (loading.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _hoisted_10,
            _hoisted_11
          ], 64))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _hoisted_12
          ], 64))
    ], 8, _hoisted_9),
    _createElementVNode("p", {
      ref: (_value, _refs) => {
        _refs['errorElement'] = _value
        errorElement.value = _value
      },
      role: "alert",
      class: "text-red-500 text-sm mt-0 flex flex-row items-center space-x-2 opacity-0 h-0 select-none pointer-events-none"
    }, [
      _createVNode(_unref(ExclamationCircleIcon), { class: "h-6 w-6 flex-shrink-0 self-start" }),
      _createElementVNode("span", null, _toDisplayString(errorMessage.value), 1)
    ], 512)
  ]))
}
}

})